import type { AccountStatus } from '@nestjs-boilerplate/types';

const GBP = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

/** The account fields the status badge is derived from. */
export interface UserStatusFields {
  deletedAt: Date | string | null;
  emailVerified: boolean;
  accountSetupComplete: boolean;
  accountStatus: AccountStatus;
}

/**
 * The single status worth showing for a user.
 *
 * Ordered, not combined. Deletion outranks everything, then the two terminal
 * administrator decisions, then the onboarding states, and only then pending
 * review. That last placement is deliberate: an applicant who has not finished
 * account setup is also pending, but there is nothing to review yet — so
 * `Pending review` reads as "complete and waiting on your decision", which
 * turns the column into a work queue rather than a status dump.
 */
export function statusOf(user: UserStatusFields): { label: string; className: string } {
  if (user.deletedAt) return { label: 'Deleted', className: 'bg-destructive/10 text-destructive' };
  if (user.accountStatus === 'REJECTED')
    return { label: 'Rejected', className: 'bg-destructive/10 text-destructive' };
  if (user.accountStatus === 'DISABLED')
    return { label: 'Disabled', className: 'bg-destructive/10 text-destructive' };
  if (!user.emailVerified)
    return { label: 'Unverified', className: 'bg-amber-500/10 text-amber-700 dark:text-amber-500' };
  if (!user.accountSetupComplete)
    return { label: 'Setup incomplete', className: 'bg-muted text-muted-foreground' };
  if (user.accountStatus === 'PENDING')
    return {
      label: 'Pending review',
      className: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
    };
  return { label: 'Active', className: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-500' };
}

/**
 * Renders a balance as currency.
 *
 * The API sends a decimal string because pence must survive the wire; Decimal
 * drops a trailing zero on the way, so '12.5' has to become £12.50 here.
 */
export function formatBalance(balance: string): string {
  return GBP.format(Number(balance));
}

/**
 * Formats a date, or reports none so the caller can choose its own fallback.
 */
export function formatDate(value: Date | string | null): string | null {
  return value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : null;
}

/**
 * Formats a timestamp, or reports none so the caller can choose its own fallback.
 */
export function formatDateTime(value: Date | string | null): string | null {
  return value ? new Date(value).toLocaleString('en-GB') : null;
}
